import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import gql from 'graphql-tag';
import {Apollo} from 'apollo-angular';
import {GetUsers, GetUsers_users_edges_node, GetUsersVariables} from './__generated__/GetUsers';
import {RoleType} from '../../shared/graphql/globalTypes';
import {BehaviorSubject, combineLatest, EMPTY, fromEvent, merge, of, Subscription} from 'rxjs';
import {catchError, debounceTime, switchMap} from 'rxjs/operators';
import {countPages} from '../../shared/utils/table.utils';
import {Router} from '@angular/router';
import {UsersGetMe} from './__generated__/UsersGetMe';
import {ErrorService} from '../../shared/services/error.service';
import {GetTotalUsersCount, GetTotalUsersCountVariables} from './__generated__/GetTotalUsersCount';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {UserDeleteComponent} from '../../shared/modals/user-delete/user-delete.component';

export const userListFrag = gql`
  fragment UserListFragment on UserType {
    id
    firstName
    lastName
    role
    function
    email
    photo
  }
`;

const usersGetQuery = gql`
  query GetUsers($roleType: RoleType, $offset: Int, $first: Int, $orderBy: String, $roles: [RoleType]){
    users(role: $roleType, offset: $offset, first: $first, orderBy: [$orderBy], role_In: $roles) {
      totalCount
      edges {
        node {
          ...UserListFragment
        }
      }
    }
  }
  ${userListFrag}
`;

const totalUsersCountQuery = gql`
  query GetTotalUsersCount($roleType: RoleType, $roles: [RoleType]) {
    users(role: $roleType, role_In: $roles) {
      totalCount
    }
  }
`;

const usersGetMeQuery = gql`
  query UsersGetMe {
    me {
      id
      role
    }
  }
`;

@Component({
  selector: 'app-user',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  standalone: false,
})
export class UsersComponent implements OnInit, OnDestroy {
  users: GetUsers_users_edges_node[] = [];
  first = new UntypedFormControl(20);
  offset = new UntypedFormControl(1);
  RoleEnum = RoleType;
  selection$ = new BehaviorSubject<RoleType | null>(null);
  pageCount = 1;
  pages: number[] = [];
  totalCount: number | null = null;
  isAdmin = false;
  userRole: RoleType | null = null;
  loading = true;
  roles: RoleType[] = [RoleType.USER, RoleType.ADMIN];
  bsModalRef: BsModalRef | null = null;
  activeDropdown: string | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private apollo: Apollo,
    private router: Router,
    private errService: ErrorService,
    private modalService: BsModalService,
  ) {}

  ngOnInit(): void {
    const currentPaging$ = merge(this.first.valueChanges, of(this.first.value));
    const offset$ = merge(this.offset.valueChanges, of(this.offset.value));
    const filterChange$ = merge(currentPaging$, this.selection$);

    const users$ = combineLatest([currentPaging$, this.selection$, offset$]).pipe(
      debounceTime(50),
      switchMap(filter => {
        return this.apollo
          .watchQuery<GetUsers, GetUsersVariables>({
            query: usersGetQuery,
            variables: {
              roleType: filter[1],
              first: filter[0],
              offset: filter[0] * (filter[2] - 1),
              roles: this.roles,
              orderBy: 'last_name',
            },
          })
          .valueChanges.pipe(
            catchError(error => {
              this.errService.handleDefault(error);
              return EMPTY;
            }),
          );
      }),
    );

    const totalCount$ = combineLatest([this.selection$]).pipe(
      debounceTime(50),
      switchMap(filter => {
        return this.apollo
          .watchQuery<GetTotalUsersCount, GetTotalUsersCountVariables>({
            query: totalUsersCountQuery,
            variables: {
              roleType: filter[0],
              roles: this.roles,
            },
          })
          .valueChanges.pipe(
            catchError(error => {
              this.errService.handleDefault(error);
              return EMPTY;
            }),
          );
      }),
    );

    this.subscriptions.push(
      this.apollo
        .watchQuery<UsersGetMe>({
          query: usersGetMeQuery,
        })
        .valueChanges.subscribe(data => {
          this.userRole = data.data?.me?.role ?? null;
        }),
      filterChange$.subscribe(() => {
        this.offset.setValue(1);
      }),
      users$.subscribe(output => {
        const users = output.data?.users;
        this.users = (users?.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : []));
        this.users = this.users.filter(user => user.role !== RoleType.SUPERADMIN);
        this.loading = false;
        this.pageCount = countPages(users?.totalCount ?? 0, this.first.value);
        this.pages = Array(this.pageCount)
          .fill(0)
          .map((x, i) => i);
      }),
      totalCount$.subscribe(output => {
        this.totalCount = output.data?.users?.totalCount ?? null;
      }),
      fromEvent(window, 'scroll').subscribe(() => (this.activeDropdown = null)),
    );
  }

  nextPage(): void {
    this.offset.setValue(this.offset.value + 1);
  }

  previousPage(): void {
    this.offset.setValue(this.offset.value - 1);
  }

  createUser(): void {
    this.router.navigate(['create-user']);
  }

  selectDropdown(id: string): void {
    if (this.activeDropdown === id) {
      this.activeDropdown = null;
      return;
    }
    this.activeDropdown = id;
  }

  navigateToProfile(id: string): void {
    this.router.navigate(['user/', id]);
  }

  navigateToProfileEdit(id: string): void {
    this.router.navigate(['edit-user/', id]);
  }

  deleteUser(id: string): void {
    this.bsModalRef = this.modalService.show(UserDeleteComponent);
    this.bsModalRef.content.id = id;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
}
